import type { ClientHomePlanBaseData } from "api-types/homePlans/homePlanResponses";
import type { ClientMailResponse } from "api-types/mail/mailResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import type { UserNotificationType } from "common/types/userNotification";
import { handleFetchError } from "common/utils/fetchHelpers";
import mergeShowroomIntroOverrides from "doc/options/mergeShowroomIntroOverrides";
import type { UseFetchNoAuthResponse } from "lib/fetcher";
import { useFetchNoAuth } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import { putBuilder } from "lib/storage/builderStorage";
import { putLocationPlan } from "lib/storage/locationPlanStorage";
import { putLocation } from "lib/storage/locationStorage";
import { updateLotCommunityPlan } from "lib/storage/lotStorage";
import { putMasterPlan } from "lib/storage/masterPlanStorage";
import type { StorageResponse } from "lib/storage/types";
import { postWithTimeout } from "lib/utils/fetchAuthHelpers";
import { buildShowroomPublicPlansUrl } from "lib/utils/showroom/publicPlansUrl";
import { useCallback } from "react";
import { useSWRConfig } from "swr";
import useAppContext from "ui/hooks/useAppContext";
import type { ShowroomState } from "../state/showroomState";
import { defaultShowroomEditState } from "../state/showroomState";
import { useSetShowroomStatePartial, useShowroomStateRef } from "../state/showroomStateContext";
import { getOverridesKey } from "../state/showroomTypes";
import {
  useIsShowroomServerMode,
  useIsShowroomWorkerMode,
  useShowroomWorkerPlanIdentifiers,
} from "../utils/showroomServerModeUtils";

export function useShowroomBasePlanDataFetchKey() {
  const { builderId, homePlanId, planType = "community_plans", planId, lotId } = useAppRouterParams();
  const isServerSideMode = useIsShowroomServerMode();
  const isWorkerMode = useIsShowroomWorkerMode();
  const workerIdentifiers = useShowroomWorkerPlanIdentifiers();

  // In server or worker mode, plan data is fetched server-side or worker-side respectively.
  // Returning "" disables the main-thread SWR fetch so the "exactly one fetch" invariant holds.
  if (isServerSideMode || (isWorkerMode && workerIdentifiers)) {
    return "";
  }

  // Saved HomePlan routes (`/showroom/plan/$home_plan_id/*`) hit the
  // authenticated base-data endpoint; both endpoints return ClientHomePlanBaseData.
  if (builderId && homePlanId) {
    return `${WEB_API_BASE_PATH}/builders/${builderId}/home-plans/${homePlanId}/base-data`;
  }

  return builderId && planId
    ? buildShowroomPublicPlansUrl({
        builderId,
        planType: planType as "community_plans" | "master_plans",
        planId,
        lotId,
      })
    : "";
}

// abstracting this into a hook utilizes SWR's caching, so this can be used multiple times without refetching endpoint
export function useShowroomBasePlanData(): UseFetchNoAuthResponse<ClientHomePlanBaseData> {
  const fetchKey = useShowroomBasePlanDataFetchKey();
  return useFetchNoAuth<ClientHomePlanBaseData>(fetchKey, [fetchKey], {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
}

export async function shareShowroomPlan(
  builderId: string,
  url: string,
  emails: string[],
): StorageResponse<{ delivered: boolean }> {
  try {
    const res = await postWithTimeout(`${WEB_API_BASE_PATH}/mail/share-plan`, {
      builderId,
      url,
      emails,
    });

    const json = (await res.json()) as ClientMailResponse;

    if (!res.ok) {
      throw json;
    }

    return [{ delivered: json.delivered }, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export function useSavePlanData() {
  // Gets state imperatively
  const showroomState = useShowroomStateRef();
  const setPatchState = useSetShowroomStatePartial();
  const { setUserNotification } = useAppContext();
  const fetchKey = useShowroomBasePlanDataFetchKey();

  const { mutate } = useSWRConfig();
  return useCallback(
    async (updatePlanOverrides: boolean) => {
      if (!showroomState.patchedPlanData) {
        return;
      }
      const { state, ...planData } = showroomState.patchedPlanData;

      if (!state?.isPatched) {
        return;
      }

      const success: string[] = [];
      const errors: UserNotificationType[] = [];
      await Promise.all(
        Object.entries(state.patchedPlanFields ?? []).map(async ([type, fields]) => {
          // Get a diff of updated fields
          const updated = {};
          const overridesKey = getOverridesKey(type as ShowroomState["planKey"]);
          for (const field in fields) {
            if (updatePlanOverrides && field === overridesKey) {
              // merge showroomIntroOverrides into overrides to update the plan
              updated[field] = mergeShowroomIntroOverrides(planData[type][field]);
            } else {
              // Update plan name also
              if (updatePlanOverrides && field === "publicName") {
                updated["name"] = planData[type][field];
              }
              updated[field] = planData[type][field];
            }
          }
          if (type === "communityPlan") {
            const [, err] = await putLocationPlan(
              planData.builder.id,
              planData.community?.id ?? "",
              planData.communityPlan.id,
              updated,
            );
            if (err) {
              errors.push(err);
            } else {
              success.push("Community plan updated.");
            }
          } else if (type === "masterPlan") {
            const [, err] = await putMasterPlan(planData.builder.id, planData.masterPlan.id, updated);
            if (err) {
              errors.push(err);
            } else {
              success.push("Master plan updated.");
            }
          } else if (type === "lotCommunityPlan") {
            // Convert field keys to match endpoint
            if (updated.hasOwnProperty("name")) {
              updated["lotCommunityPlanName"] = updated["name"];
              delete updated["name"];
            }

            if (updated.hasOwnProperty(overridesKey)) {
              updated["overrides"] = updated[overridesKey];
              delete updated[overridesKey];
            }

            const [, err] = await updateLotCommunityPlan({
              lotId: planData.lot?.id as string,
              builderId: planData.builder.id,
              locationId: planData.community?.id as string,
              lotCommunityPlanId: planData.lotCommunityPlan?.id as string,
              ...updated,
            });
            if (err) {
              errors.push(err);
            } else {
              success.push("Lot community plan updated.");
            }
          } else if (type === "builder") {
            const [, err] = await putBuilder(planData.builder.id, { salesConfig: planData.builder.salesConfig });
            if (err) {
              errors.push(err);
            } else {
              success.push("Showroom settings updated.");
            }
          } else if (type === "community") {
            const [, err] = await putLocation(planData.builder.id, planData.community?.id as string, updated);
            if (err) {
              errors.push(err);
            } else {
              success.push("Community settings updated.");
            }
          }
        }),
      );

      if (errors.length) {
        setUserNotification({
          messageType: "error",
          message: `Error! ${errors.join("\n")}`,
        });
        return;
      }

      // Refetch the data to keep it in sync with server
      void mutate(fetchKey, planData);

      /**
       * On save, reset patch history.
       * NOTE: Navigating save history with undo/redo requires some extra complexity
       * that the system is mostly setup to handle, but needs a thorough implementation.
       * Resetting the patch history is an MVP solution
       */
      setPatchState({
        patches: defaultShowroomEditState.patches,
        currentPatch: defaultShowroomEditState.currentPatch,
      });

      setUserNotification({
        messageType: "success",
        message: `Success! ${success.join("\n")}`,
      });
    },
    [fetchKey],
  );
}
